import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6122b3b6"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "input-container" }
const _hoisted_2 = {
  key: 0,
  class: "editable-list"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "shape-inputs" }

import type {Ref} from "vue";
import {computed, onBeforeUnmount, ref, watch} from "vue";
import {useStore} from "vuex";
import isUndefined from "lodash/isUndefined";
import isEmpty from "lodash/isEmpty";
import ValidationMessage from "@/components/UI/ValidationMessage.vue";
import Length from "../../../source/js/shared/models/Geometry/Length";
import { endsWithSeparator, limitDecimals, stripNonNumeric, toDecimalPoint } from "@/functions/numbers";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import EditableItem from "@/components/EditableItem.vue";
import CustomDrillHole from "@/Models/CustomDrillHole";
import InputGroup from "@/components/UI/InputGroup.vue";
import MessageContainer from "@/components/UI/MessageContainer.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'DrillHoleCustom',
  setup(__props) {

const kpsSettings = window.measurementSelectorVariables;

const activeHole:Ref<CustomDrillHole> = ref({
	x: 0,
	y: 0,
	diameter: 0,
});

const activeIndex: Ref<number|null> = ref(0);
const isEditorVisible = ref(true);
const isRemoveButtonActive = ref(false);
const isEditingExistingHole = ref(false);
const listHasHoles = ref(false);

const store = useStore();

const customDrillHoles = computed(() => store.getters.customDrillHoles );
const editableCustomDrillHoles = computed(() => customDrillHoles.value.filter( ( value: CustomDrillHole, index: number ) => index !== activeIndex.value ) );
const drillHoleDiameter = computed(() => store.getters.drillHoleDiameter.in_cm() );
const isDrillHoleValid = computed(() => store.getters.isDrillHoleValid );
const getDrillHoleValidationMessage = computed(() => store.getters.getDrillHoleValidationMessage );
const minimumDrillHoleDiameterLabel = computed(() => store.getters.minimumDrillHoleDiameterLabel );
const activeIndexPlusOne = computed(() => activeIndex.value + 1);

const x = computed({
	get: () => activeHole.value.x ? new Length( activeHole.value.x).in_cm(): activeHole.value.x,
	set: (value: string|number) => {
		if(!endsWithSeparator(value)) {
			activeHole.value.x = Length.from_cm(limitDecimals(stripNonNumeric(value), 1)).in_mm();
		}
	}
});
const y = computed({
	get: () => activeHole.value.y ? new Length( activeHole.value["y"] ).in_cm() : activeHole.value.y,
	set: (value: string|number) => {
		if(!endsWithSeparator(value)) {
			activeHole.value.y = Length.from_cm(limitDecimals(stripNonNumeric(value), 1)).in_mm();
		}
	}
});
const diameter = computed({
	get: () => activeHole.value.diameter ? new Length( activeHole.value["diameter"] ).in_cm() : activeHole.value.diameter,
	set: (value: string|number) => {
		if(!endsWithSeparator(value)) {
			activeHole.value.diameter = Length.from_cm(limitDecimals(stripNonNumeric(value), 1)).in_mm();
		}
	}
});
const getCenter = computed(() => store.getters.getCenter).value;

function created(): void {
	document.addEventListener("focusin", ( event ) =>
		store.commit("updateFocusedElement", event )
	);

	const { drillHoleSettings } = kpsSettings;

	let drillHoles = drillHoleSettings.prefilledDrillHoles;

	if (store.getters.lastGeneratedConfiguration.drillholes?.settings) {
		drillHoles = store.getters.lastGeneratedConfiguration.drillholes?.settings;
	}

	if (! isEmpty(drillHoles)) {

		// If we don't set these properties, then we don't have access to the custom drill holes in the sidebar.
		isEditorVisible.value = false;
		activeIndex.value = null;

		setRemoveButtonActiveState();

		return;
	}

	addHole();

	activeHole.value = customDrillHoles.value[activeIndex.value];
}

function getI18nNumber(value: number): string {
	return new Length(value).in_cm_i18n();
}

function removeHole(index: number): void {
	store.commit("removeCustomHole", index);
	activeIndex.value = null;

	setRemoveButtonActiveState();
}

function addHole(): void {
	const center = getCenter(true, 'mm');

	store.commit("addCustomHole", {
		x: center.x,
		y: center.y,
		diameter: Length.from_cm( drillHoleDiameter.value ).in_mm()
	});
}

function editHole( holeNumber: number ): void {
	isEditorVisible.value = true;
	isEditingExistingHole.value = true;
	activeIndex.value = holeNumber;
}

function saveHole(): void {
	setRemoveButtonActiveState();

	isEditorVisible.value = false;
	activeIndex.value = null;
}

function addNewHole(): void {
	isEditorVisible.value = true;
	isEditingExistingHole.value = false;
	activeIndex.value = customDrillHoles.value.length;

	addHole();
}

function setRemoveButtonActiveState(): void {
	isRemoveButtonActive.value = customDrillHoles.value.length > 1;
}

function setListHasHoles(index: null|number): boolean {

	// if no hole is being edited check customDrillHoles length
	if (index < 0 || index === null) {
		return customDrillHoles.value.length > 0;
	}

	// if a hole is being edited remove active hole from customDrillHoles and check length
	const holes = [...customDrillHoles.value]; // clone customDrillHoles
	holes.splice(index, 1);

	return holes.length > 0;
}

watch(activeIndex, async(index: null|number): Promise<void> => {
	activeHole.value = index !== null ? customDrillHoles.value[index] : {};
	listHasHoles.value = setListHasHoles(index);

	store.commit("updateCurrentCustomDrillhole", index);
} );

watch(activeHole, async (newValue: CustomDrillHole, oldValue: CustomDrillHole): Promise<void> => {
	for (const key in newValue) {
		if (! isUndefined(oldValue[key])) {
			store.commit("updateUsingDefaultDrillHoleDimensions", false);
		}

		customDrillHoles.value[ activeIndex.value ][ key ] =
			typeof newValue[key] === "string"
				? toDecimalPoint( newValue[ key ] )
				: newValue[key];
	}
} );

onBeforeUnmount(() => {
	document.removeEventListener("focusin", () =>
		store.commit("updateFocusedElement")
	);

	store.commit("resetCustomHoles");
	store.commit("forceRefresh");
});

created();


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (listHasHoles.value)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(editableCustomDrillHoles.value, (hole, index) => {
            return (_openBlock(), _createElementBlock("li", {
              class: "editable-list__item",
              key: `hole-${index}`
            }, [
              _createVNode(EditableItem, {
                edit: { index, title: _ctx.$i18n.drillHoleConfiguration.edit_hole, disabled: isEditorVisible.value, onClick: editHole, hidden: false, },
                remove: { index, title: _ctx.$i18n.drillHoleConfiguration.remove_hole, disabled: !isRemoveButtonActive.value, onClick: removeHole, hidden: false }
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$i18n.drillHoleConfiguration.singular_hole) + " " + _toDisplayString(index + 1) + " ", 1),
                  _createElementVNode("strong", null, " X - " + _toDisplayString(getI18nNumber( hole.x )) + " | Y - " + _toDisplayString(getI18nNumber( hole.y )) + " | " + _toDisplayString(_ctx.$i18n.drillHoleConfiguration.diameter_drill_hole_short) + " - " + _toDisplayString(getI18nNumber( hole.diameter )), 1)
                ]),
                _: 2
              }, 1032, ["edit", "remove"])
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (isEditorVisible.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(DimensionInput, {
              inputRef: "drillHoleOffsetX",
              id: "drillHoleOffsetX",
              name: "drillHoleOffsetX",
              unit: "cm",
              label: _ctx.$i18n.drillHoleConfiguration.distance_from_edge_left,
              invalid: !isDrillHoleValid.value('drillHoleOffsetX', activeIndexPlusOne.value),
              modelValue: x.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((x).value = $event))
            }, null, 8, ["label", "invalid", "modelValue"]),
            _createVNode(DimensionInput, {
              inputRef: "drillHoleOffsetY",
              id: "drillHoleOffsetY",
              name: "drillHoleOffsetY",
              unit: "cm",
              label: _ctx.$i18n.drillHoleConfiguration.distance_from_edge_bottom,
              invalid: !isDrillHoleValid.value('drillHoleOffsetY', activeIndexPlusOne.value),
              modelValue: y.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((y).value = $event))
            }, null, 8, ["label", "invalid", "modelValue"]),
            _createVNode(DimensionInput, {
              inputRef: "drillHoleDiameter",
              id: "drillHoleDiameter",
              name: "drillHoleDiameter",
              unit: "cm",
              label: `${_ctx.$i18n.drillHoleConfiguration.diameter_drill_hole} (${minimumDrillHoleDiameterLabel.value})`,
              invalid: !isDrillHoleValid.value('drillHoleDiameter', activeIndexPlusOne.value),
              modelValue: diameter.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((diameter).value = $event))
            }, null, 8, ["label", "invalid", "modelValue"]),
            _createVNode(InputGroup, { "flex-justify": "bottom" }, {
              default: _withCtx(() => [
                _createElementVNode("button", {
                  type: "button",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => {saveHole();}),
                  class: "measurements-selector__button-add"
                }, _toDisplayString(isEditingExistingHole.value ? _ctx.$i18n.drillHoleConfiguration.edit_hole : _ctx.$i18n.drillHoleConfiguration.add_hole), 1)
              ]),
              _: 1
            })
          ]),
          _createVNode(MessageContainer, null, {
            default: _withCtx(() => [
              (!isDrillHoleValid.value(`drillHoleDiameter_${activeIndexPlusOne.value}`))
                ? (_openBlock(), _createBlock(ValidationMessage, {
                    key: 0,
                    "input-id": "drillHoleDiameter"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(getDrillHoleValidationMessage.value(`drillHoleDiameter_${activeIndexPlusOne.value}`)), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (!isDrillHoleValid.value(`drillHoleOffsetX`, activeIndexPlusOne.value))
                ? (_openBlock(), _createBlock(ValidationMessage, {
                    key: 1,
                    "input-id": "drillHoleOffsetX"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(getDrillHoleValidationMessage.value(`drillHoleOffsetX`, activeIndexPlusOne.value)), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (!isDrillHoleValid.value(`drillHoleOffsetY`, activeIndexPlusOne.value))
                ? (_openBlock(), _createBlock(ValidationMessage, {
                    key: 2,
                    "input-id": "drillHoleOffsetY"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(getDrillHoleValidationMessage.value(`drillHoleOffsetY`, activeIndexPlusOne.value)), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    (!isEditorVisible.value)
      ? (_openBlock(), _createBlock(InputGroup, {
          key: 2,
          "flex-justify": "bottom"
        }, {
          default: _withCtx(() => [
            _createElementVNode("button", {
              type: "button",
              onClick: _cache[4] || (_cache[4] = ($event: any) => {addNewHole();}),
              class: "measurements-selector__button-add"
            }, _toDisplayString(_ctx.$i18n.drillHoleConfiguration.add_hole_long), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (! isDrillHoleValid.value(`drillHoleUnique_${activeIndexPlusOne.value}`))
      ? (_openBlock(), _createBlock(ValidationMessage, { key: 3 }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(getDrillHoleValidationMessage.value(`drillHoleUnique_${activeIndexPlusOne.value}`)), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (! isDrillHoleValid.value(`drillHoleMissing_${activeIndexPlusOne.value}`))
      ? (_openBlock(), _createBlock(ValidationMessage, { key: 4 }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(getDrillHoleValidationMessage.value(`drillHoleMissing_${activeIndexPlusOne.value}`)), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})